/**
 * Unit conversion definitions and helpers for display-only conversion.
 * Stored data is never modified — conversions are applied at render time.
 *
 * Three conversion types:
 * 1. LINEAR — within same measurement system (nM ↔ µM)
 * 2. CROSS-SYSTEM — molar ↔ mass/volume (nM ↔ ng/mL, needs molecular weight)
 * 3. LOGARITHMIC — IC50 ↔ pIC50 (concentration ↔ negative log scale)
 */

export interface UnitGroup {
  unitType: string;
  units: string[];
  convert: (value: number, fromUnit: string, toUnit: string) => number;
}

// ── Base factors (relative to SI-ish base) ──

const MOLAR_BASES: Record<string, number> = {
  pM: 1e-12, nM: 1e-9, 'µM': 1e-6, uM: 1e-6, mM: 1e-3, M: 1,
};

const MASS_BASES: Record<string, number> = {
  'pg/mL': 1e-12, 'ng/mL': 1e-9, 'µg/mL': 1e-6, 'ug/mL': 1e-6, 'mg/mL': 1e-3, 'g/L': 1,
};

const TIME_BASES: Record<string, number> = {
  s: 1, min: 60, h: 3600, d: 86400,
};

const WEIGHT_BASES: Record<string, number> = {
  Da: 1, kDa: 1000, MDa: 1e6,
};

const PERMEABILITY_BASES: Record<string, number> = {
  'nm/s': 1e-7, 'cm/s': 1,
};

// ── Linear unit groups ──

const UNIT_GROUPS: Record<string, UnitGroup> = {
  concentration_molar: {
    unitType: 'concentration_molar',
    units: ['pM', 'nM', 'µM', 'mM', 'M'],
    convert: (value, from, to) => {
      const fromBase = MOLAR_BASES[from] ?? MOLAR_BASES[from.replace('µ', 'u')] ?? 1;
      const toBase = MOLAR_BASES[to] ?? MOLAR_BASES[to.replace('µ', 'u')] ?? 1;
      return value * (fromBase / toBase);
    },
  },
  concentration_mass: {
    unitType: 'concentration_mass',
    units: ['pg/mL', 'ng/mL', 'µg/mL', 'mg/mL', 'g/L'],
    convert: (value, from, to) => {
      const fromBase = MASS_BASES[from] ?? MASS_BASES[from.replace('µ', 'u')] ?? 1;
      const toBase = MASS_BASES[to] ?? MASS_BASES[to.replace('µ', 'u')] ?? 1;
      return value * (fromBase / toBase);
    },
  },
  weight: {
    unitType: 'weight',
    units: ['Da', 'kDa', 'MDa'],
    convert: (value, from, to) => {
      const fromBase = WEIGHT_BASES[from] ?? 1;
      const toBase = WEIGHT_BASES[to] ?? 1;
      return value * (fromBase / toBase);
    },
  },
  time: {
    unitType: 'time',
    units: ['s', 'min', 'h', 'd'],
    convert: (value, from, to) => {
      const fromBase = TIME_BASES[from] ?? 1;
      const toBase = TIME_BASES[to] ?? 1;
      return value * (fromBase / toBase);
    },
  },
  permeability: {
    unitType: 'permeability',
    units: ['nm/s', 'cm/s'],
    convert: (value, from, to) => {
      const fromBase = PERMEABILITY_BASES[from] ?? 1;
      const toBase = PERMEABILITY_BASES[to] ?? 1;
      return value * (fromBase / toBase);
    },
  },
};

// ── Logarithmic conversion pairs ──
// pActivity = -log10(activity in M). These are potency/binding endpoints;
// percentage efficacy endpoints such as Emax are deliberately not log-converted.

export type LogConversionDirection = 'to_log' | 'from_log';

export interface LogPair {
  partner: string;
  direction: LogConversionDirection;
}

const LOG_ENDPOINTS = [
  ['IC50', 'pIC50'],
  ['EC50', 'pEC50'],
  ['AC50', 'pAC50'],
  ['IC90', 'pIC90'],
  ['IC99', 'pIC99'],
  ['GI50', 'pGI50'],
  ['CC50', 'pCC50'],
  ['LC50', 'pLC50'],
  ['Ki', 'pKi'],
  ['Kd', 'pKd'],
  ['Kb', 'pKb'],
] as const;

export const LOG_PAIRS: Record<string, LogPair> = Object.fromEntries(
  LOG_ENDPOINTS.flatMap(([linearName, logName]) => [
    [linearName.toLowerCase(), { partner: logName, direction: 'to_log' as const }],
    [logName.toLowerCase(), { partner: `${linearName} (nM)`, direction: 'from_log' as const }],
  ]),
);

function molarFactor(unit: string): number | null {
  const normalised = unit.trim().replace(/[μµ]/g, 'u');
  return MOLAR_BASES[normalised] ?? null;
}

/**
 * Convert a molar concentration to its negative base-10 logarithm.
 * Defaults to nM for backwards compatibility.
 * pIC50 = -log10(IC50 in M) = 9 - log10(IC50 in nM)
 */
export function convertToNegLog(value: number, fromUnit = 'nM'): number {
  const factor = molarFactor(fromUnit);
  if (value <= 0 || !Number.isFinite(value) || factor == null) return NaN;
  return -Math.log10(value * factor);
}

/**
 * Convert a negative-log potency/binding value back to a molar concentration.
 * Defaults to nM for backwards compatibility.
 */
export function convertFromNegLog(pValue: number, toUnit = 'nM'): number {
  const factor = molarFactor(toUnit);
  if (!Number.isFinite(pValue) || factor == null) return NaN;
  return Math.pow(10, -pValue) / factor;
}

/** Extract a supported molar unit from labels such as "IC50 (nM)". */
export function getMolarUnitFromLabel(label: string): string | null {
  const match = label.match(/(?:^|[_\s(])(pM|nM|[uµ]M|mM|M)(?:\)|$)/i);
  if (!match) return null;
  const unit = match[1].replace('u', 'µ');
  if (/^pm$/i.test(unit)) return 'pM';
  if (/^nm$/i.test(unit)) return 'nM';
  if (/^[µu]m$/i.test(unit)) return 'µM';
  if (/^mm$/i.test(unit)) return 'mM';
  return 'M';
}

// ── Cross-system: molar ↔ mass/volume (needs MW) ──

/**
 * Convert from molar unit to mass/volume unit using molecular weight.
 * Steps: value in fromUnit → base M → g/mL (via MW) → toUnit
 */
export function convertMolarToMass(
  value: number,
  fromUnit: string,
  toUnit: string,
  mw: number,
): number {
  if (!mw || mw <= 0 || !isFinite(value)) return NaN;
  const fromBase = MOLAR_BASES[fromUnit] ?? MOLAR_BASES[fromUnit.replace('µ', 'u')] ?? null;
  const toBase = MASS_BASES[toUnit] ?? MASS_BASES[toUnit.replace('µ', 'u')] ?? null;
  if (fromBase == null || toBase == null) return NaN;
  // value * fromBase = concentration in M
  // M * MW(Da) = g/L (since mol/L × g/mol = g/L)
  // g/L = g/mL × 1000, but our MASS_BASES for g/L = 1
  // So: value_M × MW = value in g/L (which is our mass base)
  const baseM = value * fromBase;
  const baseGperL = baseM * mw; // g/L
  return baseGperL / toBase;
}

/**
 * Convert from mass/volume unit to molar unit using molecular weight.
 */
export function convertMassToMolar(
  value: number,
  fromUnit: string,
  toUnit: string,
  mw: number,
): number {
  if (!mw || mw <= 0 || !isFinite(value)) return NaN;
  const fromBase = MASS_BASES[fromUnit] ?? MASS_BASES[fromUnit.replace('µ', 'u')] ?? null;
  const toBase = MOLAR_BASES[toUnit] ?? MOLAR_BASES[toUnit.replace('µ', 'u')] ?? null;
  if (fromBase == null || toBase == null) return NaN;
  const baseGperL = value * fromBase; // g/L
  const baseM = baseGperL / mw; // M
  return baseM / toBase;
}

// ── Public API (preserves existing interface) ──

/**
 * Get list of compatible units for a given unit type.
 * Returns empty array for dimensionless/percentage/area or unknown types.
 */
export function getCompatibleUnits(unitType: string | null | undefined): string[] {
  if (!unitType) return [];
  return UNIT_GROUPS[unitType]?.units ?? [];
}

/**
 * Convert a numeric value between compatible units (linear only).
 */
export function convertValue(
  value: number,
  fromUnit: string,
  toUnit: string,
  unitType: string,
): number {
  if (fromUnit === toUnit) return value;
  const group = UNIT_GROUPS[unitType];
  if (!group) return value;
  return group.convert(value, fromUnit, toUnit);
}

/**
 * Format an axis label with unit: "IC50 (nM)" or just "LogP" if no unit.
 */
export function formatAxisLabel(
  propertyName: string,
  unit: string | null | undefined,
): string {
  if (!unit) return propertyName;
  return `${propertyName} (${unit})`;
}

/**
 * Unit override for display-only conversion (not persisted).
 */
export interface UnitOverride {
  originalUnit: string;
  displayUnit: string;
  unitType: string;
}

// ── Conversion options for right-click menu ──

export type ConversionType = 'linear' | 'cross_system' | 'logarithmic';

export interface ConversionOption {
  label: string;
  targetUnit: string;
  type: ConversionType;
  needsMW: boolean;
  isAddColumn: boolean;
}

/**
 * Get all available conversions for a column, including:
 * - Linear within-group (display toggle + add column)
 * - Cross-system molar↔mass (display toggle + add column, needs MW)
 * - Logarithmic IC50↔pIC50 (add column only)
 */
export function getAvailableConversions(
  currentUnit: string | null,
  unitType: string | null,
  fieldName: string,
  hasMW: boolean,
): ConversionOption[] {
  const options: ConversionOption[] = [];

  // 1. Linear conversions within same group (display toggle)
  if (unitType && UNIT_GROUPS[unitType]) {
    const group = UNIT_GROUPS[unitType];
    for (const unit of group.units) {
      if (unit !== currentUnit) {
        options.push({
          label: `Display as ${unit}`,
          targetUnit: unit,
          type: 'linear',
          needsMW: false,
          isAddColumn: false,
        });
      }
    }
  }

  // 2. Cross-system: molar → mass units (needs MW)
  if (unitType === 'concentration_molar' && hasMW) {
    for (const massUnit of UNIT_GROUPS.concentration_mass.units) {
      options.push({
        label: `Display as ${massUnit}`,
        targetUnit: massUnit,
        type: 'cross_system',
        needsMW: true,
        isAddColumn: false,
      });
    }
  }
  // Cross-system: mass → molar units (needs MW)
  if (unitType === 'concentration_mass' && hasMW) {
    for (const molarUnit of UNIT_GROUPS.concentration_molar.units) {
      options.push({
        label: `Display as ${molarUnit}`,
        targetUnit: molarUnit,
        type: 'cross_system',
        needsMW: true,
        isAddColumn: false,
      });
    }
  }

  // 3. Logarithmic: IC50 ↔ pIC50 (add column only)
  const logPair = getLogPartner(fieldName);
  if (logPair) {
    const pair = logPair;
    options.push({
      label: `Add column: ${pair.partner}`,
      targetUnit: pair.partner,
      type: 'logarithmic',
      needsMW: false,
      isAddColumn: true,
    });
  }

  // 4. Add "add column" variants for all display-toggle options
  const displayOptions = [...options.filter(o => !o.isAddColumn)];
  for (const opt of displayOptions) {
    options.push({
      ...opt,
      label: `Add column: ${opt.targetUnit}`,
      isAddColumn: true,
    });
  }

  return options;
}

/**
 * Check if a field name has a logarithmic conversion partner.
 * Strips common unit suffixes (e.g. IC50_nM → IC50) before lookup.
 */
export function getLogPartner(fieldName: string): LogPair | null {
  const lower = fieldName.trim().toLowerCase();
  // Direct match first
  if (LOG_PAIRS[lower]) return LOG_PAIRS[lower];
  // Strip common unit suffixes: _nM, _uM, _µM, (nM), etc.
  const stripped = lower
    .replace(/[_\s(]*(pm|nm|[uµ]m|mm|m)[)\s]*$/i, '')
    .replace(/[_()\s]+$/, '');
  return LOG_PAIRS[stripped] ?? null;
}

/**
 * Check if a unit type is molar concentration (for cross-system detection).
 */
export function isMolarUnit(unitType: string | null | undefined): boolean {
  return unitType === 'concentration_molar';
}

/**
 * Check if a unit type is mass concentration (for cross-system detection).
 */
export function isMassUnit(unitType: string | null | undefined): boolean {
  return unitType === 'concentration_mass';
}
